import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Chip,
  Typography,
} from "@mui/material";
import { useShareTemplates } from "../hooks/useShareMessageTemplates";
import { ShareTemplate, TemplateFormData } from "../types/share-template.types";
import CustomTextField from "../../../../components/common/Textfield";

interface CreateTemplateModalProps {
  open: boolean;
  onClose: () => void;
  editTemplate?: ShareTemplate | null;
  onSave: () => void;
}

const EXAMPLE_VALUES: Record<string, string> = {
  deceasedName: "John Doe",
  eventName: "Wedding Ceremony",
  date: "Tuesday 23 December 2025",
  time: "14:30",
  venue: "Harare Convention Centre",
  address: "123 Main Street, Harare",
  website_stream_link: "https://example.com/stream",
};

const VALID_PLACEHOLDERS = Object.keys(EXAMPLE_VALUES);

const CreateTemplateModal: React.FC<CreateTemplateModalProps> = ({
  open,
  onClose,
  editTemplate,
  onSave,
}) => {
  const { saveTemplate, updateTemplate, loading } = useShareTemplates();

  const [templateData, setTemplateData] = useState<TemplateFormData>({
    name: "",
    category: "funeral",
    body_template: "",
  });

  useEffect(() => {
    if (editTemplate) {
      setTemplateData({
        name: editTemplate.name,
        category: editTemplate.category,
        body_template: editTemplate.body_template,
      });
    } else {
      setTemplateData({
        name: "",
        category: "funeral",
        body_template: "",
      });
    }
  }, [editTemplate]);

  const handleChange = (field: keyof ShareTemplate, value: string) => {
    setTemplateData((prev) => ({ ...prev, [field]: value }));
  };

  const insertPlaceholder = (placeholder: string) => {
    const textarea = document.getElementById(
      "highlighted-textarea"
    ) as HTMLTextAreaElement | null;

    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;

    const newValue =
      templateData.body_template.slice(0, start) +
      placeholder +
      templateData.body_template.slice(end);

    setTemplateData((prev) => ({ ...prev, body_template: newValue }));

    setTimeout(() => {
      textarea.selectionStart = textarea.selectionEnd =
        start + placeholder.length;
      textarea.focus();
    }, 0);
  };

  const highlightVariables = (text: string) => {
    let result = text;

    VALID_PLACEHOLDERS.forEach((key) => {
      const placeholder = `{${key}}`;

      result = result.replaceAll(
        placeholder,
        `<span style="color: red; font-weight: bold">${EXAMPLE_VALUES[key]}</span>`
      );
    });

    return result;
  };

  const handleSave = async () => {
    if (editTemplate?.id) {
      await updateTemplate(editTemplate.id, templateData);
    } else {
      await saveTemplate(templateData);
    }
    onSave();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {editTemplate ? "Edit Template" : "Create Template"}
      </DialogTitle>

      <DialogContent>
        <Box display="flex" gap={3}>
          <Box flex={2} display="flex" flexDirection="column" gap={3}>
            <CustomTextField
              label="Template Name"
              fullWidth
              value={templateData.name}
              onChange={(e) => handleChange("name", e.target.value)}
            />

            <CustomTextField
              id="highlighted-textarea"
              label="Message Body"
              multiline
              minRows={8}
              value={templateData.body_template}
              onChange={(e) => handleChange("body_template", e.target.value)}
            />

            <Typography variant="subtitle2" color="textSecondary">
              These are the variables you can use in your message:
            </Typography>

            <Box display="flex" flexWrap="wrap" gap={1}>
              {VALID_PLACEHOLDERS.map((p) => (
                <Chip
                  key={p}
                  label={`{${p}}`}
                  onClick={() => insertPlaceholder(`{${p}}`)}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>

          <Box
            flex={1}
            p={2}
            border="1px solid #ddd"
            borderRadius={2}
            bgcolor="#fafafa"
            height="100%"
            maxHeight="450px"
            overflow="auto"
            boxShadow="inset 0 0 4px rgba(0,0,0,0.1)"
          >
            <Typography fontWeight="bold" mb={1}>
              Live Preview
            </Typography>

            <Typography
              component="div"
              whiteSpace="pre-wrap"
              dangerouslySetInnerHTML={{
                __html: highlightVariables(templateData.body_template),
              }}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button disabled={loading} onClick={handleSave} variant="contained">
          {editTemplate ? "Update" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateTemplateModal;
